import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Languages, FileText, Globe, Shield, Zap, ArrowRight, CheckCircle, Star, BookOpen, FileType, Clock, Award, Upload, Download, Loader2, XCircle } from 'lucide-react';
import Footer from './Footer';
import SEO from './SEO';

const API_BASE_URL = (() => {
  const url = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/+$/, '');
  return url.endsWith('/api') ? url : `${url}/api`;
})();

const LandingPage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // Çeviri durumu
  const [selectedFile, setSelectedFile] = useState(null);
  const [sourceLang, setSourceLang] = useState('en');
  const [targetLang, setTargetLang] = useState('tr');
  const [translating, setTranslating] = useState(false);
  const [translatedText, setTranslatedText] = useState('');
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);

  const features = [
    {
      icon: <FileText className="w-8 h-8" />,
      title: "Çoklu Format Desteği",
      description: "PDF, TXT, EPUB ve DOCX dosyalarınızı kolayca işleyin."
    },
    {
      icon: <Globe className="w-8 h-8" />,
      title: "100+ Dil Desteği",
      description: "Dünya genelinde 100'den fazla dil arasında çeviri yapın."
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Hızlı İşleme",
      description: "Gelişmiş algoritmalarımız ile belgelerinizi saniyeler içinde işleyin."
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Güvenli ve Gizli",
      description: "Belgeleriniz güvenli sunucularda işlenir ve silinir."
    }
  ];

  const supportedFormats = [
    { name: 'PDF', icon: '📄', description: 'Taranmış ve dijital PDF' },
    { name: 'TXT', icon: '📝', description: 'Düz metin dosyaları' },
    { name: 'EPUB', icon: '📚', description: 'E-kitap formatı' },
    { name: 'DOCX', icon: '📋', description: 'Word belgeleri' }
  ];

  const stats = [
    { value: '100+', label: 'Dil Desteği', icon: <Globe className="w-5 h-5" /> },
    { value: '1M+', label: 'Çevrilen Sayfa', icon: <FileType className="w-5 h-5" /> },
    { value: '%99.9', label: 'Doğruluk', icon: <Award className="w-5 h-5" /> },
    { value: '7/24', label: 'Destek', icon: <Clock className="w-5 h-5" /> }
  ];

  const testimonials = [
    {
      name: "Ayşe Yılmaz",
      role: "Akademisyen",
      text: "Tezlerimi İngilizce'ye çevirmek için kullanıyorum. Harika sonuçlar!",
      rating: 5
    },
    {
      name: "Mehmet Kaya",
      role: "İşletme Sahibi",
      text: "Müşteri belgelerimi hızlıca çeviriyorum. Zamandan çok tasarruf ediyorum.",
      rating: 5
    },
    {
      name: "Zeynep Demir",
      role: "Öğrenci",
      text: "Ders notlarımı farklı dillere çevirmek için mükemmel bir araç.",
      rating: 5
    }
  ];

  const LANDING_SCHEMA = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "CeviriPro",
    "url": "https://ceviripro.com.tr/",
    "applicationCategory": "UtilitiesApplication",
    "description": "PDF, DOCX, EPUB ve TXT dosyalarınızı 100'den fazla dile saniyeler içinde çevirin.",
    "offers": { "@type": "Offer", "price": "0", "priceCurrency": "TRY" }
  };

  // Dosya seçme
  const handleFileSelect = (file) => {
    if (!file) return;

    const validTypes = ['.pdf', '.txt', '.epub', '.docx'];
    const fileExt = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));

    if (!validTypes.includes(fileExt)) {
      setError('Sadece PDF, TXT, EPUB ve DOCX dosyaları destekleniyor!');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Dosya boyutu 5MB\'dan küçük olmalıdır!');
      return;
    }

    setSelectedFile(file);
    setError('');
    setTranslatedText('');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  // Çeviri işlemi - sadece ilk 2 sayfa
  const handleTranslate = async () => {
    if (!selectedFile) return;

    setTranslating(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('operation', 'translate');
      formData.append('source_lang', sourceLang);
      formData.append('target_lang', targetLang);
      formData.append('output_format', 'txt');
      formData.append('preview_only', 'true'); // Önizleme modu - sadece ilk kısım

      const response = await fetch(`${API_BASE_URL}/process`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Çeviri başarısız');
      }

      // Önizleme metni (backend'den gelen ilk kısım)
      setTranslatedText(data.preview_text || 'Çeviri tamamlandı! Tam sonuç için kaydolun.');

    } catch (err) {
      setError(err.message || 'Çeviri sırasında bir hata oluştu.');
    } finally {
      setTranslating(false);
    }
  };

  // İndir butonu - kaydola yönlendir
  const handleDownload = () => {
    navigate('/register');
  };

  return (
    <div className="min-h-screen bg-soft-blue">
      <SEO
        title="PDF, DOCX, EPUB Belge Çeviri | 100+ Dil Desteği"
        description="CeviriPro ile PDF, Word (DOCX), EPUB ve TXT dosyalarınızı 100'den fazla dile saniyeler içinde çevirin."
        canonical="/"
        schema={LANDING_SCHEMA}
      />

      {/* Navbar */}
      <nav className="glass sticky top-0 z-50 border-b border-blue-100/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <img src="/logo192.png" alt="CeviriPro Logo" className="w-10 h-10 object-contain drop-shadow-sm" />
              <span className="text-xl font-bold text-gradient-blue">
                ÇeviriPro
              </span>
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="#ozellikler" className="text-slate-600 hover:text-blue-600 font-medium transition-colors">
                Özellikler
              </a>
              <a href="#desteklenen-formatlar" className="text-slate-600 hover:text-blue-600 font-medium transition-colors">
                Formatlar
              </a>
              <Link to="/fiyatlandirma" className="text-slate-600 hover:text-blue-600 font-medium transition-colors">
                Fiyatlandırma
              </Link>
              <a href="#yorumlar" className="text-slate-600 hover:text-blue-600 font-medium transition-colors">
                Yorumlar
              </a>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="/login"
                className="px-4 py-2 text-slate-600 hover:text-blue-600 font-medium transition-colors"
              >
                Giriş Yap
              </Link>
              <Link
                to="/register"
                className="px-5 py-2 bg-gradient-to-r from-blue-400 to-indigo-400 text-white rounded-xl font-medium hover:shadow-lg transition-all"
              >
                Kaydol
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-12 pb-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 border border-blue-100 rounded-full text-blue-600 text-sm font-medium mb-6 shadow-sm">
              <Zap className="w-4 h-4" />
              Yapay Zeka Destekli Çeviri
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-5 leading-tight">
              Belgelerinizi{' '}
              <span className="bg-gradient-to-r from-blue-400 via-indigo-400 to-cyan-400 bg-clip-text text-transparent">
                Saniyeler İçinde
              </span>
              {' '}Çevirin
            </h1>

            <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
              PDF, EPUB, DOCX ve TXT dosyalarınızı 100'den fazla dile çevirin.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-8">
              <Link
                to="/register"
                className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-blue-400 to-indigo-400 text-white rounded-xl font-semibold text-base hover:shadow-lg transition-all flex items-center justify-center gap-2"
              >
                Hemen Başla
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-slate-500">
              <div className="flex items-center gap-1.5">
                <CheckCircle className="w-4 h-4 text-blue-400" />
                Ücretsiz deneme
              </div>
              <div className="flex items-center gap-1.5">
                <CheckCircle className="w-4 h-4 text-blue-400" />
                Kayıt gerekmez
              </div>
              <div className="flex items-center gap-1.5">
                <CheckCircle className="w-4 h-4 text-blue-400" />
                Hızlı sonuç
              </div>
            </div>
          </div>

          {/* Fonksiyonel Çeviri Örneği */}
          <div className="max-w-3xl mx-auto">
            <div className="bg-white/90 rounded-2xl shadow-lg border border-blue-100 overflow-hidden">
              <div className="p-4 border-b border-blue-50 bg-gradient-to-r from-blue-50 to-indigo-50">
                <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                  <Languages className="w-5 h-5 text-blue-500" />
                  Hızlı Çeviri Önizleme
                </h3>
              </div>

              <div className="p-4">
                {/* Dil Seçimi */}
                <div className="flex items-center gap-3 mb-4">
                  <select
                    value={sourceLang}
                    onChange={(e) => setSourceLang(e.target.value)}
                    className="flex-1 p-2.5 border border-blue-100 rounded-lg text-sm focus:border-blue-300 focus:outline-none bg-white"
                  >
                    <option value="en">🇬🇧 İngilizce</option>
                    <option value="tr">🇹🇷 Türkçe</option>
                    <option value="de">🇩🇪 Almanca</option>
                    <option value="fr">🇫🇷 Fransızca</option>
                    <option value="es">🇪🇸 İspanyolca</option>
                  </select>
                  <ArrowRight className="w-5 h-5 text-blue-400" />
                  <select
                    value={targetLang}
                    onChange={(e) => setTargetLang(e.target.value)}
                    className="flex-1 p-2.5 border border-blue-100 rounded-lg text-sm focus:border-blue-300 focus:outline-none bg-white"
                  >
                    <option value="tr">🇹🇷 Türkçe</option>
                    <option value="en">🇬🇧 İngilizce</option>
                    <option value="de">🇩🇪 Almanca</option>
                    <option value="fr">🇫🇷 Fransızca</option>
                    <option value="es">🇪🇸 İspanyolca</option>
                  </select>
                </div>

                {/* Dosya Yükleme Alanı */}
                <div
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all mb-4 ${dragActive
                    ? 'border-blue-400 bg-blue-50'
                    : selectedFile
                      ? 'border-blue-300 bg-blue-50/50'
                      : 'border-blue-200 hover:border-blue-300'
                    }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.txt,.epub,.docx"
                    onChange={(e) => handleFileSelect(e.target.files?.[0])}
                    className="hidden"
                  />

                  {selectedFile ? (
                    <div className="flex items-center justify-center gap-3">
                      <FileText className="w-8 h-8 text-blue-500" />
                      <div className="text-left">
                        <p className="font-medium text-slate-800 text-sm">{selectedFile.name}</p>
                        <p className="text-xs text-slate-500">{(selectedFile.size / 1024).toFixed(1)} KB • Max 5MB</p>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); setSelectedFile(null); setTranslatedText(''); }}
                        className="p-1 hover:bg-blue-100 rounded"
                      >
                        <XCircle className="w-5 h-5 text-slate-400" />
                      </button>
                    </div>
                  ) : (
                    <div>
                      <Upload className="w-10 h-10 text-blue-300 mx-auto mb-2" />
                      <p className="text-sm text-slate-600">Dosya yükleyin (Max 5MB)</p>
                      <p className="text-xs text-slate-400 mt-1">PDF, TXT, EPUB, DOCX</p>
                    </div>
                  )}
                </div>

                {/* Hata Mesajı */}
                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-lg text-red-600 text-sm">
                    {error}
                  </div>
                )}

                {/* Çeviri Butonu */}
                <button
                  onClick={handleTranslate}
                  disabled={!selectedFile || translating}
                  className="w-full py-3 bg-gradient-to-r from-blue-400 to-indigo-400 text-white rounded-xl font-medium hover:shadow-md transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {translating ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Çeviriliyor...
                    </>
                  ) : (
                    <>
                      <Zap className="w-5 h-5" />
                      Çeviri Yap (Önizleme)
                    </>
                  )}
                </button>

                {/* Çeviri Sonucu */}
                {translatedText && (
                  <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-blue-700">Çeviri Önizleme</span>
                      <span className="text-xs text-blue-500">İlk Kısım (Maksimum 2 Sayfa)</span>
                    </div>
                    <div className="relative">
                      <p className="text-sm text-slate-700 max-h-48 overflow-y-auto mb-3 whitespace-pre-wrap pr-2">
                        {translatedText}
                      </p>
                      <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-blue-50 to-transparent pointer-events-none mb-3"></div>
                    </div>
                    <button
                      onClick={handleDownload}
                      className="w-full py-2.5 bg-white border border-blue-200 text-blue-600 rounded-lg font-medium text-sm hover:bg-blue-50 transition-all flex items-center justify-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Tüm Belgeyi Çevirmek İçin Kayıt Olun
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-10 bg-white/50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center w-10 h-10 bg-blue-50 rounded-xl mb-2">
                  <div className="text-blue-500">{stat.icon}</div>
                </div>
                <div className="text-2xl font-bold text-gradient-blue">{stat.value}</div>
                <div className="text-slate-500 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="ozellikler" className="py-16 bg-white/50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">
              Neden ÇeviriPro?
            </h2>
            <p className="text-slate-600 max-w-xl mx-auto">
              En gelişmiş çeviri teknolojisini kullanarak belgelerinizi kolayca çevirin
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {features.map((feature, index) => (
              <div key={index} className="card-soft p-5 rounded-xl">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl flex items-center justify-center text-blue-500 mb-3">
                  {feature.icon}
                </div>
                <h3 className="text-base font-bold text-slate-900 mb-1.5">
                  {feature.title}
                </h3>
                <p className="text-sm text-slate-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Supported Formats - Marquee */}
      <section id="desteklenen-formatlar" className="py-12 bg-blue-50/50 overflow-hidden">
        <div className="text-center mb-8">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
            Desteklenen Formatlar
          </h2>
          <p className="text-slate-600">
            Tüm popüler doküman formatlarını destekliyoruz
          </p>
        </div>

        <div className="relative flex overflow-hidden">
          <div className="animate-marquee flex gap-4 whitespace-nowrap">
            {[...supportedFormats, ...supportedFormats, ...supportedFormats].map((format, index) => (
              <div
                key={index}
                className="flex-shrink-0 bg-white p-4 rounded-xl border border-blue-100"
              >
                <div className="text-3xl mb-2">{format.icon}</div>
                <h3 className="text-base font-bold text-slate-900">
                  {format.name}
                </h3>
                <p className="text-xs text-slate-500">
                  {format.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="nasil-calisir" className="py-16 bg-white/50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">
              Nasıl Çalışır?
            </h2>
            <p className="text-slate-600 max-w-xl mx-auto">
              Sadece 3 adımda belgelerinizi çevirin
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center text-blue-600 text-2xl font-bold mx-auto mb-3">
                1
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-1.5">
                Dosyanızı Yükleyin
              </h3>
              <p className="text-slate-600 text-sm">
                PDF, TXT, EPUB veya DOCX dosyanızı seçin
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center text-green-600 text-2xl font-bold mx-auto mb-3">
                2
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-1.5">
                Dili Seçin
              </h3>
              <p className="text-slate-600 text-sm">
                Kaynak ve hedef dili seçin
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center text-indigo-600 text-2xl font-bold mx-auto mb-3">
                3
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-1.5">
                İndirin
              </h3>
              <p className="text-slate-600 text-sm">
                Çeviriyi anında indirin
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="yorumlar" className="py-16 bg-blue-50/30">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">
              Kullanıcı Yorumları
            </h2>
            <p className="text-slate-600 max-w-xl mx-auto">
              Binlerce mutlu kullanıcımız var
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="card-soft p-5 rounded-xl">
                <div className="flex gap-1 mb-3">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-slate-600 text-sm mb-3">"{testimonial.text}"</p>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-300 to-indigo-300 rounded-full flex items-center justify-center text-white text-sm font-bold">
                    {testimonial.name[0]}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800 text-sm">{testimonial.name}</p>
                    <p className="text-xs text-slate-500">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-400 via-indigo-400 to-cyan-400">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
            Hemen Başlayın
          </h2>
          <p className="text-blue-100 mb-6">
            Binlerce kullanıcı belgelerini ÇeviriPro ile çeviriyor. Siz de hemen deneyin!
          </p>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-blue-500 rounded-2xl font-semibold text-base hover:shadow-lg transition-all"
          >
            Ücretsiz Kaydol
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default LandingPage;